import React, { useState, useEffect } from 'react';
import { transactionAPI, userAPI } from '../services/api';
import { 
  ArrowUpTrayIcon,
  CreditCardIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  WalletIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const Withdraw = () => {
  const [amount, setAmount] = useState('');
  const [balance, setBalance] = useState(0);
  const [selectedMethod, setSelectedMethod] = useState('crypto');
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [walletLoading, setWalletLoading] = useState(false);
  const [fee, setFee] = useState(0);
  const [finalAmount, setFinalAmount] = useState(0);
  const [errors, setErrors] = useState({});
  const [platformCut, setPlatformCut] = useState(0);
  const [isWeekend, setIsWeekend] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const MIN_WITHDRAWAL = 100;
  const MAX_WITHDRAWAL = 500;
  const PLATFORM_CUT_PERCENTAGE = 18;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    fetchBalance();
    fetchWallets();
    checkIfWeekend();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    calculateFeeAndAmount();
    validateAmount();
  }, [amount, selectedMethod, balance]);

  const checkIfWeekend = () => {
    const today = new Date();
    const day = today.getDay();
    const isWeekendDay = (day === 0 || day === 6);
    setIsWeekend(isWeekendDay);
  };

  const fetchBalance = async () => {
    try {
      const response = await userAPI.getBalance();
      setBalance(response.data.balance);
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  const fetchWallets = async () => {
    try {
      setWalletLoading(true);
      const response = await userAPI.getWallets();
      if (response.data.success) {
        const cryptoWallets = response.data.wallets.filter(wallet => wallet.type === 'crypto');
        setWallets(cryptoWallets || []);
        const defaultWallet = cryptoWallets.find(w => w.isDefault);
        if (defaultWallet) setSelectedWallet(defaultWallet.id);
      }
    } catch (error) {
      console.error('Error fetching wallets:', error);
    } finally {
      setWalletLoading(false);
    }
  };

  const withdrawMethods = [
    { 
      id: 'crypto', 
      name: 'Cryptocurrency', 
      icon: CreditCardIcon, 
      color: '#00d4ff',
      bg: 'rgba(0, 212, 255, 0.1)',
      fee: '0.5%',
      platformCut: `${PLATFORM_CUT_PERCENTAGE}%`,
      time: '48-72 hours',
      min: MIN_WITHDRAWAL,
      max: MAX_WITHDRAWAL
    }
  ];

  const calculateFeeAndAmount = () => {
    const amountNum = parseFloat(amount) || 0;
    const selected = withdrawMethods.find(m => m.id === selectedMethod);
    if (!selected) return;
    
    const feePercentage = parseFloat(selected.fee) / 100;
    const calculatedFee = amountNum * feePercentage;
    const calculatedPlatformCut = amountNum * (PLATFORM_CUT_PERCENTAGE / 100);
    const calculatedFinal = amountNum - calculatedFee - calculatedPlatformCut;
    
    setFee(calculatedFee);
    setPlatformCut(calculatedPlatformCut);
    setFinalAmount(calculatedFinal);
  };

  const validateAmount = () => {
    const newErrors = {};
    const amountNum = parseFloat(amount) || 0;

    if (amount) {
      if (amountNum < MIN_WITHDRAWAL) newErrors.min = `Minimum withdrawal is $${MIN_WITHDRAWAL}`;
      if (amountNum > MAX_WITHDRAWAL) newErrors.max = `Maximum withdrawal is $${MAX_WITHDRAWAL}`;
      if (amountNum > balance) newErrors.balance = 'Insufficient balance';
      
      const totalDeductions = (amountNum * (PLATFORM_CUT_PERCENTAGE / 100)) + (amountNum * 0.005);
      if (amountNum - totalDeductions < 0.01) newErrors.deductions = 'Amount too low after deductions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleWithdraw = async () => {
    if (isWeekend) {
      alert('Withdrawals are not processed on weekends (Saturday & Sunday). Please try again on Monday.');
      return;
    }

    if (!amount || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    if (!validateAmount()) return;

    const selectedMethodInfo = withdrawMethods.find(m => m.id === selectedMethod);
    if (Number(amount) < selectedMethodInfo.min || Number(amount) > selectedMethodInfo.max) {
      alert(`Withdrawal amount must be between $${selectedMethodInfo.min} and $${selectedMethodInfo.max}`);
      return;
    }

    const confirmationMessage = `💰 Withdrawal Confirmation:\n──────────────────────────\nAmount: $${Number(amount).toFixed(2)}\nPlatform Cut (${PLATFORM_CUT_PERCENTAGE}%): -$${platformCut.toFixed(2)}\nProcessing Fee (${selectedMethodInfo.fee}): -$${fee.toFixed(2)}\n──────────────────────────\nYou Receive: $${finalAmount.toFixed(2)}\n\n⏰ Processing Time: 48-72 hours\n\nDo you want to proceed?`;

    if (!window.confirm(confirmationMessage)) return;

    if (!selectedWallet) {
      alert('Please select a crypto wallet');
      return;
    }

    const selectedWalletObj = wallets.find(w => w.id === selectedWallet);
    if (!selectedWalletObj) {
      alert('Selected wallet not found');
      return;
    }

    const cryptoDetails = {
      address: selectedWalletObj.address,
      currency: selectedWalletObj.currency || 'USDT',
      network: selectedWalletObj.network || 'ERC20',
      walletName: selectedWalletObj.name || 'My Crypto Wallet'
    };

    try {
      setLoading(true);
      await transactionAPI.withdraw({
        amount: Number(amount),
        method: selectedMethod,
        walletId: selectedWallet,
        cryptoDetails: cryptoDetails,
        platformCut: platformCut,
        finalAmount: finalAmount
      });
      
      alert('Withdrawal request submitted successfully! Processing time: 48-72 hours.');
      setAmount('');
      fetchBalance();
    } catch (error) {
      console.error('Withdrawal error:', error);
      alert(error.response?.data?.message || 'Failed to process withdrawal');
    } finally {
      setLoading(false);
    }
  };

  const quickAmounts = [100, 250, 500];

  const isWithdrawDisabled = () => {
    const amountNum = parseFloat(amount) || 0;
    return loading || isWeekend || !amount || amountNum < MIN_WITHDRAWAL || amountNum > MAX_WITHDRAWAL || amountNum > balance || !selectedWallet;
  };

  const getButtonText = () => {
    if (isWeekend) return '⛔ Not Available on Weekends';
    if (loading) return '⏳ Processing...';
    if (!amount) return '💰 Enter an amount';
    if (parseFloat(amount) < MIN_WITHDRAWAL) return `⚠️ Minimum $${MIN_WITHDRAWAL}`;
    if (parseFloat(amount) > MAX_WITHDRAWAL) return `⚠️ Maximum $${MAX_WITHDRAWAL}`;
    if (parseFloat(amount) > balance) return '⚠️ Insufficient Balance';
    if (!selectedWallet) return '⚠️ Select a wallet';
    return `💸 Withdraw $${amount || '0.00'}`;
  };

  const getDisabledReason = () => {
    if (isWeekend) return 'Weekend - No Withdrawals';
    if (!amount) return 'No amount entered';
    if (parseFloat(amount) < MIN_WITHDRAWAL) return `Amount below minimum ($${MIN_WITHDRAWAL})`;
    if (parseFloat(amount) > MAX_WITHDRAWAL) return `Amount above maximum ($${MAX_WITHDRAWAL})`;
    if (parseFloat(amount) > balance) return 'Insufficient balance';
    if (!selectedWallet) return 'No wallet selected';
    return '';
  };

  const getDeductionBreakdown = () => {
    const amountNum = parseFloat(amount) || 0;
    if (amountNum <= 0) return null;

    const totalDeductions = platformCut + fee;
    const totalPercentage = ((totalDeductions / amountNum) * 100).toFixed(1);
    
    return (
      <div style={{
        marginTop: '20px',
        padding: '20px',
        background: '#0f0f0f',
        borderRadius: '16px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px',
          paddingBottom: '12px',
          borderBottom: '1px solid rgba(239, 68, 68, 0.3)'
        }}>
          <ExclamationTriangleIcon style={{ width: '20px', height: '20px', color: '#ef4444' }} />
          <h4 style={{ color: '#ef4444', margin: 0, fontSize: '16px', fontWeight: '600' }}>Deduction Breakdown</h4>
        </div>
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '10px 0',
            borderBottom: '1px solid rgba(0, 212, 255, 0.1)'
          }}>
            <span style={{ color: '#94a3b8' }}>Platform Fee ({PLATFORM_CUT_PERCENTAGE}%)</span>
            <span style={{ color: '#ef4444' }}>-${platformCut.toFixed(2)}</span>
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '10px 0',
            borderBottom: '1px solid rgba(0, 212, 255, 0.1)'
          }}>
            <span style={{ color: '#94a3b8' }}>Processing Fee ({withdrawMethods.find(m => m.id === selectedMethod)?.fee})</span>
            <span style={{ color: '#ef4444' }}>-${fee.toFixed(2)}</span>
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '12px 0',
            marginTop: '8px',
            borderTop: '2px solid rgba(0, 212, 255, 0.2)'
          }}>
            <span style={{ fontWeight: '600', color: 'white' }}>Total Deductions ({totalPercentage}%)</span>
            <span style={{ fontWeight: '700', color: '#ef4444', fontSize: '18px' }}>-${totalDeductions.toFixed(2)}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      padding: isMobile ? '16px' : '20px',
      maxWidth: '1200px',
      margin: '0 auto',
      paddingBottom: isMobile ? '80px' : '20px',
      backgroundColor: '#0a0a0a',
      minHeight: '100vh'
    }}>
      {/* Header - Dark Theme */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
        borderRadius: '24px',
        padding: '24px',
        color: 'white',
        marginBottom: '24px',
        boxShadow: '0 10px 30px rgba(0, 212, 255, 0.2)',
        border: '1px solid rgba(0, 212, 255, 0.3)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '16px',
          marginBottom: '20px'
        }}>
          <div>
            <h1 style={{ 
              fontSize: isMobile ? '28px' : '32px', 
              fontWeight: '700', 
              margin: '0 0 8px 0',
              background: 'linear-gradient(135deg, #fff, #00d4ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>Withdraw Funds</h1>
            <p style={{ opacity: '0.7', margin: 0, fontSize: '14px' }}>Transfer to your crypto wallet</p>
          </div>
          <ArrowUpTrayIcon style={{ width: '32px', height: '32px', color: '#00d4ff' }} />
        </div>
        
        <div style={{
          background: 'rgba(0, 212, 255, 0.1)',
          borderRadius: '16px',
          padding: '16px',
          border: '1px solid rgba(0, 212, 255, 0.2)'
        }}>
          <p style={{ fontSize: '14px', opacity: '0.7', margin: '0 0 8px 0' }}>Available Balance</p>
          <p style={{ fontSize: '32px', fontWeight: '700', margin: 0, color: '#00d4ff' }}>${balance.toFixed(2)}</p>
          <p style={{ fontSize: '12px', opacity: '0.7', margin: '8px 0 0 0' }}>Min: ${MIN_WITHDRAWAL} | Max: ${MAX_WITHDRAWAL}</p>
        </div>
      </div>

      {/* Weekend Warning - Dark Theme */}
      {isWeekend && (
        <div style={{
          marginBottom: '20px',
          padding: '16px 20px',
          background: 'rgba(245, 158, 11, 0.15)',
          borderRadius: '16px',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <ExclamationTriangleIcon style={{ width: '24px', height: '24px', color: '#f59e0b', flexShrink: 0 }} />
          <div>
            <strong style={{ fontSize: '16px', color: '#f59e0b' }}>Weekend Notice:</strong>
            <p style={{ margin: '5px 0 0 0', color: '#94a3b8', fontSize: '14px' }}>
              Withdrawals are not processed on weekends (Saturday & Sunday). Please try again on Monday.
            </p>
          </div>
        </div>
      )}

      {/* Processing Time Banner */}
      <div style={{
        marginBottom: '20px',
        padding: '16px 20px',
        background: 'rgba(0, 212, 255, 0.1)',
        borderRadius: '16px',
        border: '1px solid rgba(0, 212, 255, 0.3)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <ClockIcon style={{ width: '24px', height: '24px', color: '#00d4ff', flexShrink: 0 }} />
        <div>
          <strong style={{ fontSize: '16px', color: '#00d4ff' }}>⏰ Processing Time:</strong>
          <p style={{ margin: '5px 0 0 0', color: '#94a3b8', fontSize: '14px' }}>
            Withdrawals are processed within <strong style={{ color: '#fbbf24' }}>48-72 hours</strong> (Monday-Friday only)
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Amount Section - Dark Theme */}
        <div style={{
          background: '#1a1a2e',
          borderRadius: '24px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          padding: '24px',
          opacity: isWeekend ? 0.6 : 1
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#00d4ff', marginBottom: '20px' }}>Amount to Withdraw</h2>
          
          <div style={{ position: 'relative', marginBottom: '16px' }}>
            <CurrencyDollarIcon style={{
              position: 'absolute',
              left: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '20px',
              height: '20px',
              color: isWeekend ? '#64748b' : '#00d4ff'
            }} />
            <input
              type="number"
              value={amount}
              onChange={(e) => {
                const regex = /^\d*\.?\d*$/;
                if (e.target.value === '' || regex.test(e.target.value)) setAmount(e.target.value);
              }}
              placeholder="0.00"
              style={{
                width: '100%',
                padding: '16px 80px 16px 48px',
                fontSize: '24px',
                fontWeight: '700',
                color: isWeekend ? '#64748b' : 'white',
                background: '#0f0f0f',
                border: isWeekend ? '1px solid #ef4444' : '1px solid rgba(0, 212, 255, 0.3)',
                borderRadius: '16px',
                outline: 'none',
                cursor: isWeekend ? 'not-allowed' : 'text'
              }}
              min={MIN_WITHDRAWAL}
              max={MAX_WITHDRAWAL}
              step="0.01"
              disabled={isWeekend}
            />
            <button 
              onClick={() => {
                const maxAllowed = Math.min(balance, MAX_WITHDRAWAL);
                if (maxAllowed >= MIN_WITHDRAWAL) setAmount(maxAllowed.toFixed(2));
              }}
              disabled={balance < MIN_WITHDRAWAL || isWeekend}
              style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: '#ef4444',
                border: 'none',
                borderRadius: '8px',
                padding: '6px 12px',
                color: 'white',
                fontWeight: '600',
                cursor: (balance < MIN_WITHDRAWAL || isWeekend) ? 'not-allowed' : 'pointer',
                opacity: (balance < MIN_WITHDRAWAL || isWeekend) ? 0.5 : 1
              }}
            >
              MAX
            </button>
          </div>
          
          {/* Validation Errors */}
          {errors.min && <p style={{ color: '#f87171', fontSize: '13px', marginBottom: '8px' }}>{errors.min}</p>}
          {errors.max && <p style={{ color: '#f87171', fontSize: '13px', marginBottom: '8px' }}>{errors.max}</p>}
          {errors.balance && <p style={{ color: '#f87171', fontSize: '13px', marginBottom: '8px' }}>{errors.balance}</p>}
          {errors.deductions && <p style={{ color: '#f87171', fontSize: '13px', marginBottom: '8px' }}>{errors.deductions}</p>}
          
          <p style={{ fontSize: '14px', color: '#94a3b8', marginBottom: '20px' }}>
            Available: <span style={{ color: '#00d4ff', fontWeight: '600' }}>${balance.toFixed(2)}</span>
            <span style={{ color: '#64748b' }}> (Min: ${MIN_WITHDRAWAL}, Max: ${MAX_WITHDRAWAL})</span>
          </p>
          
          {/* Quick Amounts */}
          <div>
            <p style={{ fontSize: '14px', color: '#94a3b8', marginBottom: '12px' }}>Quick Select</p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '8px'
            }}>
              {quickAmounts.map(amt => (
                <button
                  key={amt}
                  onClick={() => setAmount(amt.toString())}
                  disabled={amt > balance || amt < MIN_WITHDRAWAL || amt > MAX_WITHDRAWAL || isWeekend}
                  style={{
                    padding: '12px',
                    background: Number(amount) === amt ? '#ef4444' : '#0f0f0f',
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    borderRadius: '12px',
                    color: Number(amount) === amt ? 'white' : '#94a3b8',
                    fontWeight: '500',
                    cursor: (amt > balance || amt < MIN_WITHDRAWAL || amt > MAX_WITHDRAWAL || isWeekend) ? 'not-allowed' : 'pointer',
                    opacity: (amt > balance || amt < MIN_WITHDRAWAL || amt > MAX_WITHDRAWAL || isWeekend) ? 0.5 : 1
                  }}
                >
                  ${amt}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Method Selection - Dark Theme */}
        <div style={{
          background: '#1a1a2e',
          borderRadius: '24px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          padding: '24px',
          opacity: isWeekend ? 0.6 : 1
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#00d4ff', marginBottom: '20px' }}>Withdrawal Method</h2>
          {isWeekend && (
            <div style={{
              marginBottom: '16px',
              padding: '8px 12px',
              background: 'rgba(239, 68, 68, 0.1)',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <span style={{ color: '#f87171', fontSize: '13px' }}>🔒 Method selection disabled on weekends</span>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {withdrawMethods.map(method => {
              const Icon = method.icon;
              return (
                <button
                  key={method.id}
                  onClick={() => setSelectedMethod(method.id)}
                  disabled={isWeekend}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '20px',
                    background: selectedMethod === method.id ? 'rgba(0, 212, 255, 0.1)' : '#0f0f0f',
                    border: selectedMethod === method.id ? '2px solid #00d4ff' : '1px solid rgba(0, 212, 255, 0.2)',
                    borderRadius: '16px',
                    cursor: isWeekend ? 'not-allowed' : 'pointer',
                    opacity: isWeekend ? 0.6 : 1,
                    textAlign: 'left',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{
                    width: '56px',
                    height: '56px',
                    background: method.bg,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                    flexShrink: 0
                  }}>
                    <Icon style={{ width: '28px', height: '28px', color: method.color }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ fontSize: '18px', fontWeight: '600', color: 'white', marginBottom: '8px' }}>{method.name}</h4>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                      <span style={{
                        background: 'rgba(0, 212, 255, 0.2)',
                        color: '#00d4ff',
                        padding: '4px 8px',
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>Fee: {method.fee}</span>
                      <span style={{
                        background: 'rgba(245, 158, 11, 0.2)',
                        color: '#f59e0b',
                        padding: '4px 8px',
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>Platform: {method.platformCut}</span>
                      <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        background: 'rgba(0, 212, 255, 0.2)',
                        color: '#00d4ff',
                        padding: '4px 8px',
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>
                        <ClockIcon style={{ width: '12px', height: '12px' }} />
                        {method.time}
                      </span>
                    </div>
                    <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0 }}>${method.min} - ${method.max}</p>
                  </div>
                  {selectedMethod === method.id && (
                    <CheckCircleIcon style={{ width: '24px', height: '24px', color: '#00d4ff', flexShrink: 0 }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Terms and Conditions Link */}
        <div style={{
          marginBottom: '20px',
          padding: '16px 20px',
          background: 'rgba(0, 212, 255, 0.05)',
          borderRadius: '16px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <DocumentTextIcon style={{ width: '24px', height: '24px', color: '#00d4ff' }} />
            <div>
              <p style={{ margin: 0, color: '#94a3b8', fontSize: '13px' }}>
                <strong style={{ color: '#ff6666' }}>⚠️ Fraud Prevention:</strong> Any attempt to reinvest without adding new funds will result in 50% penalty and account ban.
              </p>
            </div>
          </div>
          <Link to="/terms" style={{
            color: '#00d4ff',
            textDecoration: 'none',
            fontSize: '13px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '6px 12px',
            background: 'rgba(0, 212, 255, 0.1)',
            borderRadius: '20px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(0, 212, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(0, 212, 255, 0.1)';
          }}>
            Read Terms & Conditions →
          </Link>
        </div>

        {/* Wallet Selection - Dark Theme */}
        <div style={{
          background: '#1a1a2e',
          borderRadius: '24px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          padding: '24px',
          opacity: isWeekend ? 0.6 : 1
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#00d4ff', margin: 0 }}>Select Crypto Wallet</h2>
            <button 
              onClick={fetchWallets} 
              disabled={isWeekend}
              style={{
                padding: '8px 16px',
                background: '#0f0f0f',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                borderRadius: '10px',
                color: '#00d4ff',
                cursor: isWeekend ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: isWeekend ? 0.5 : 1
              }}
            >
              <ArrowPathIcon style={{ width: '16px', height: '16px', animation: walletLoading ? 'spin 1s linear infinite' : 'none' }} />
              Refresh
            </button>
          </div>
          
          {isWeekend && (
            <div style={{
              marginBottom: '16px',
              padding: '8px 12px',
              background: 'rgba(239, 68, 68, 0.1)',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <span style={{ color: '#f87171', fontSize: '13px' }}>🔒 Wallet selection disabled on weekends</span>
            </div>
          )}
          
          {walletLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <ArrowPathIcon style={{ width: '32px', height: '32px', color: '#00d4ff', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
              <p style={{ color: '#94a3b8' }}>Loading wallets...</p>
            </div>
          ) : wallets.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {wallets.map(wallet => (
                <div
                  key={wallet.id}
                  onClick={() => !isWeekend && setSelectedWallet(wallet.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px',
                    background: selectedWallet === wallet.id ? 'rgba(0, 212, 255, 0.1)' : '#0f0f0f',
                    border: selectedWallet === wallet.id ? '2px solid #00d4ff' : '1px solid rgba(0, 212, 255, 0.2)',
                    borderRadius: '16px',
                    cursor: isWeekend ? 'not-allowed' : 'pointer',
                    opacity: isWeekend ? 0.6 : 1
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: '#00d4ff',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                    flexShrink: 0
                  }}>
                    <WalletIcon style={{ width: '24px', height: '24px', color: '#1a1a2e' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#00d4ff', margin: 0 }}>{wallet.name}</h4>
                      {wallet.isDefault && (
                        <span style={{
                          background: 'rgba(0, 212, 255, 0.2)',
                          color: '#00d4ff',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: '600'
                        }}>Default</span>
                      )}
                    </div>
                    <p style={{
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      color: '#94a3b8',
                      margin: '0 0 8px 0',
                      wordBreak: 'break-all'
                    }}>
                      {wallet.address.substring(0, 30)}...
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {wallet.network && (
                        <span style={{
                          background: 'rgba(0, 212, 255, 0.2)',
                          color: '#00d4ff',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: '600'
                        }}>{wallet.network}</span>
                      )}
                      {wallet.currency && (
                        <span style={{
                          background: 'rgba(245, 158, 11, 0.2)',
                          color: '#f59e0b',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: '600'
                        }}>{wallet.currency}</span>
                      )}
                    </div>
                  </div>
                  {selectedWallet === wallet.id && (
                    <CheckCircleIcon style={{ width: '24px', height: '24px', color: '#00d4ff', flexShrink: 0 }} />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <CreditCardIcon style={{ width: '64px', height: '64px', color: '#64748b', margin: '0 auto 16px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'white', marginBottom: '8px' }}>No crypto wallets found</h3>
              <p style={{ color: '#94a3b8', marginBottom: '20px' }}>Add a crypto wallet in your profile settings</p>
              <button 
                onClick={() => window.location.href = '/profile?tab=wallets'}
                disabled={isWeekend}
                style={{
                  padding: '12px 24px',
                  background: '#00d4ff',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#1a1a2e',
                  cursor: isWeekend ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  opacity: isWeekend ? 0.5 : 1
                }}
              >
                Go to Wallets
              </button>
            </div>
          )}
        </div>

        {/* Summary - Dark Theme */}
        {amount > 0 && !isWeekend && (
          <div style={{
            background: '#1a1a2e',
            borderRadius: '24px',
            border: '2px solid #ef4444',
            padding: '24px'
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#00d4ff', marginBottom: '20px' }}>Withdrawal Summary</h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 0',
                borderBottom: '1px solid rgba(0, 212, 255, 0.1)'
              }}>
                <span style={{ color: '#94a3b8' }}>Withdrawal Amount</span>
                <span style={{ color: 'white', fontWeight: '600' }}>${Number(amount).toFixed(2)}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 0',
                borderBottom: '1px solid rgba(0, 212, 255, 0.1)'
              }}>
                <span>Platform Fee <span style={{ color: '#94a3b8', fontSize: '12px' }}>({PLATFORM_CUT_PERCENTAGE}%)</span></span>
                <span style={{ color: '#ef4444' }}>-${platformCut.toFixed(2)}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 0',
                borderBottom: '1px solid rgba(0, 212, 255, 0.1)'
              }}>
                <span>Processing Fee <span style={{ color: '#94a3b8', fontSize: '12px' }}>({withdrawMethods.find(m => m.id === selectedMethod)?.fee})</span></span>
                <span style={{ color: '#ef4444' }}>-${fee.toFixed(2)}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 0',
                borderBottom: '1px solid rgba(0, 212, 255, 0.1)'
              }}>
                <span style={{ color: '#94a3b8' }}>Processing Time</span>
                <span style={{ color: '#fbbf24', fontWeight: '600' }}>{withdrawMethods.find(m => m.id === selectedMethod)?.time}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '12px 0',
                borderBottom: '1px solid rgba(0, 212, 255, 0.1)'
              }}>
                <span style={{ color: '#94a3b8' }}>Limits</span>
                <span style={{ color: '#f59e0b' }}>${MIN_WITHDRAWAL} - ${MAX_WITHDRAWAL}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '16px 0',
                marginTop: '8px',
                borderTop: '2px solid rgba(0, 212, 255, 0.3)',
                background: 'rgba(0, 212, 255, 0.05)',
                borderRadius: '12px',
                marginTop: '8px'
              }}>
                <span style={{ fontWeight: '700', color: 'white', fontSize: '18px' }}>You Receive</span>
                <span style={{ fontWeight: '700', color: '#00d4ff', fontSize: '24px' }}>${finalAmount.toFixed(2)}</span>
              </div>
            </div>
            
            {getDeductionBreakdown()}
            
            <div style={{
              marginTop: '20px',
              padding: '16px',
              background: 'rgba(245, 158, 11, 0.1)',
              borderRadius: '12px',
              display: 'flex',
              gap: '12px',
              border: '1px solid rgba(245, 158, 11, 0.3)'
            }}>
              <ExclamationTriangleIcon style={{ width: '20px', height: '20px', color: '#f59e0b', flexShrink: 0 }} />
              <div>
                <p style={{ margin: 0, color: '#f59e0b', fontSize: '13px', fontWeight: '500' }}>Important:</p>
                <p style={{ margin: '4px 0 0 0', color: '#94a3b8', fontSize: '12px' }}>The platform fee of {PLATFORM_CUT_PERCENTAGE}% is deducted from all withdrawals.</p>
              </div>
            </div>
          </div>
        )}

        {/* Important Info - Dark Theme */}
        <div style={{
          background: '#1a1a2e',
          borderRadius: '24px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          padding: '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <ShieldCheckIcon style={{ width: '24px', height: '24px', color: '#00d4ff' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#00d4ff', margin: 0 }}>Important Information</h3>
          </div>
          <ul style={{
            margin: 0,
            paddingLeft: '20px',
            color: '#94a3b8',
            fontSize: '14px',
            lineHeight: '1.8'
          }}>
            <li>Minimum withdrawal: <strong style={{ color: '#00d4ff' }}>${MIN_WITHDRAWAL}</strong></li>
            <li>Maximum withdrawal: <strong style={{ color: '#00d4ff' }}>${MAX_WITHDRAWAL}</strong></li>
            <li><strong style={{ color: '#ef4444' }}>{PLATFORM_CUT_PERCENTAGE}% platform fee</strong> applies to all withdrawals</li>
            <li>Additional 0.5% processing fee for crypto withdrawals</li>
            <li><strong style={{ color: '#fbbf24' }}>⏰ Processing time: 48-72 hours</strong> (Monday-Friday only)</li>
            <li><strong style={{ color: '#f59e0b' }}>No withdrawals on weekends (Saturday & Sunday)</strong></li>
            <li>Fees are deducted from withdrawal amount</li>
            <li>Ensure crypto wallet address is correct</li>
            <li>Double-check network compatibility</li>
            <li><Link to="/terms" style={{ color: '#00d4ff', textDecoration: 'none' }}>Read Terms & Conditions</Link> for fraud prevention policies</li>
          </ul>
        </div>

        {/* Withdraw Button - Dark Theme with clear reason */}
        <div>
          <button
            onClick={handleWithdraw}
            disabled={isWithdrawDisabled()}
            style={{
              width: '100%',
              padding: '18px',
              background: isWithdrawDisabled() ? '#334155' : '#ef4444',
              border: 'none',
              borderRadius: '20px',
              color: 'white',
              fontSize: '18px',
              fontWeight: '600',
              cursor: isWithdrawDisabled() ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              opacity: isWithdrawDisabled() ? 0.7 : 1
            }}
            onMouseEnter={(e) => {
              if (!isWithdrawDisabled()) {
                e.target.style.background = '#f87171';
              }
            }}
            onMouseLeave={(e) => {
              if (!isWithdrawDisabled()) {
                e.target.style.background = '#ef4444';
              }
            }}
          >
            {loading ? (
              <>
                <ArrowPathIcon style={{ width: '20px', height: '20px', animation: 'spin 1s linear infinite' }} />
                Processing...
              </>
            ) : (
              getButtonText()
            )}
          </button>
          
          {/* Show disabled reason below button */}
          {isWithdrawDisabled() && !loading && (
            <div style={{
              marginTop: '12px',
              padding: '10px',
              background: 'rgba(245, 158, 11, 0.1)',
              borderRadius: '12px',
              textAlign: 'center',
              border: '1px solid rgba(245, 158, 11, 0.3)'
            }}>
              <p style={{ 
                margin: 0, 
                color: '#f59e0b', 
                fontSize: '13px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: '8px' 
              }}>
                <ExclamationTriangleIcon style={{ width: '16px', height: '16px' }} />
                {getDisabledReason()}
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        body {
          background-color: #0a0a0a;
        }
      `}</style>
    </div>
  );
};

export default Withdraw;